import { useState, type FormEvent } from "react";
import { useUserContext, darfVorgang, darfJetzt, type UserContext } from "@/auth/operations";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut, LayoutDashboard, Bot, Users, Radio, Server, Box, SlidersHorizontal, Paperclip, KeyRound,
  Plug, Blocks, Cog, ScrollText, ShieldCheck, Sun, Moon, Search, Bell, FolderGit2, BadgeCheck, LayoutTemplate,
  Workflow,
  ListChecks,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useTokenRefresh } from "@/auth/useTokenRefresh";
import { useTheme } from "@/theme/ThemeContext";
import { useAgents } from "@/api/queries/agents";
import { useChannels } from "@/api/queries/channels";
import { useNodes } from "@/api/queries/nodes";
import { cn } from "@/lib/utils";

// #design-pass W2: App-Shell — dunkle Sidebar (gruppiert, Live-Problem-Badges) + Topbar.
const PROBLEM_PHASES = ["crashed", "crashloopbackoff", "degraded"];

// #299: `op` = der VORGANG, der diesen Bereich sichtbar macht (der Server entscheidet).
type NavEntry = { to: string; label: string; icon: LucideIcon; end?: boolean; countKey?: "agents" | "channels" | "nodes"; op?: string };
const NAV_GROUPS: Array<{ group: string | null; items: NavEntry[] }> = [
  { group: null, items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, op: "dashboard.view" }] },
  {
    group: "Betrieb",
    items: [
      { to: "/agents", label: "Agents", icon: Bot, countKey: "agents", op: "agents.view" },
      { to: "/channels", label: "Channels", icon: Radio, countKey: "channels", op: "channels.view" },
      { to: "/nodes", label: "Nodes", icon: Server, countKey: "nodes", op: "nodes.view" },
      // #301 (UC-5b): Läufe sind Betrieb, keine Verwaltung — hier schaut jemand nach, was
      // gerade passiert, nicht was eingerichtet ist.
      { to: "/cases", label: "Läufe", icon: ListChecks, op: "cases.view" },
    ],
  },
  {
    group: "Verwaltung",
    items: [
      { to: "/users", label: "Users", icon: Users, op: "users.view" },
      // #299: welcher Vorgang einen Bereich sichtbar macht, entscheidet der SERVER
      // (config/operations.ts) — hier steht nur noch sein Name.
      { to: "/roles", label: "Rollen", icon: BadgeCheck, op: "roles.view" },
      { to: "/processes", label: "Prozesse", icon: Workflow, op: "processes.view" }, // #282
      { to: "/brain-images", label: "Brain-Images", icon: Box, op: "brain-images.view" },
      { to: "/settings-profiles", label: "Settings-Profile", icon: SlidersHorizontal, op: "settings-profiles.view" },
      { to: "/attachments", label: "Attachments", icon: Paperclip, op: "attachments.view" },
      { to: "/anthropic-auth", label: "Anthropic-Auth", icon: KeyRound, op: "anthropic-auth.view" },
      { to: "/connectors", label: "Connectors", icon: Plug, op: "connectors.view" },
      { to: "/plugins", label: "Plugins", icon: Blocks, op: "plugins.view" },
      // #290 (UC-2): Fach-Sicht (Entity-Ebene) VOR der technischen Repo-Werkbank.
      { to: "/workspace-templates", label: "Workspace-Vorlagen", icon: LayoutTemplate, op: "workspace-templates.view" },
      { to: "/repositories", label: "Repositories", icon: FolderGit2, op: "repositories.view" },
      { to: "/platform-settings", label: "Plattform-Settings", icon: Cog, op: "platform-settings.view" },
      { to: "/license", label: "Lizenz", icon: ShieldCheck, op: "license.view" },
      { to: "/audit", label: "Audit", icon: ScrollText, op: "audit.read" },
    ],
  },
];

// #295/#299: Nicht-Admins sehen nur ihren Arbeitsbereich — alles andere wäre eine
// 403-Wüste. Kein Rechte-Modell im Client (das enforced der Server); der Filter ist reine
// Bedienbarkeit.
//
// #299 ersetzt den früheren `role === "admin"`-Vergleich samt fester Pfad-Ausnahmeliste:
// der Server sagt, welche VORGÄNGE erlaubt sind, und die Navigation zeigt genau die
// Bereiche dazu. Die alte Liste log, sobald jemand per System-Rolle mehr lesen durfte, als
// sie kannte — ein plugin-author hat z.B. `git-content:read` und sieht deshalb jetzt auch
// die Vorlagen/Repositories, die er tatsächlich öffnen darf.
function sichtbareNavGruppen(ctx: UserContext | undefined): typeof NAV_GROUPS {
  return NAV_GROUPS.map((sec) => ({
    ...sec,
    items: sec.items.filter((i) => !i.op || darfVorgang(ctx, i.op)),
  })).filter((sec) => sec.items.length > 0);
}

const TITLES: Record<string, string> = {
  "": "Leitwarte",
  agents: "Agents",
  channels: "Channels",
  nodes: "Nodes",
  cases: "Läufe",
  users: "Users",
  roles: "Rollen",
  processes: "Prozesse",
  "brain-images": "Brain-Images",
  "settings-profiles": "Settings-Profile",
  attachments: "Attachments",
  "anthropic-auth": "Anthropic-Auth",
  connectors: "Connectors",
  plugins: "Plugins",
  "workspace-templates": "Workspace-Vorlagen",
  repositories: "Repositories",
  "platform-settings": "Plattform-Settings",
  license: "Lizenz",
  audit: "Audit-Log",
};

type Count = { value: number; tone: "danger" | "muted" };

function MeshLogo() {
  return (
    <div className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[8px] bg-gradient-to-br from-accent to-[#7c93f5] shadow-[0_2px_8px_rgba(58,85,217,.4)]">
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="5" r="2" /><circle cx="5" cy="18" r="2" /><circle cx="19" cy="18" r="2" />
        <path d="M12 7v4M10.5 13 6.5 16M13.5 13l4 3" />
      </svg>
    </div>
  );
}

function NavRow({ item, count }: { item: NavEntry; count?: Count }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "")}`}
      className={({ isActive }) =>
        cn(
          "group relative mb-0.5 flex items-center gap-3 rounded-[8px] px-2.5 py-2 text-[13.5px] transition-colors",
          isActive
            ? "bg-sidebar-active font-semibold text-sidebar-active-text"
            : "font-medium text-sidebar-text hover:bg-white/5",
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && <span className="absolute -left-3 bottom-2 top-2 w-[3px] rounded bg-accent" />}
          <Icon className={cn("h-[17px] w-[17px]", isActive ? "text-accent" : "")} />
          <span className="flex-1">{item.label}</span>
          {count && (
            <span
              className={cn(
                "grid h-[18px] min-w-[18px] place-items-center rounded-md px-1.5 text-[11px] font-bold tabular-nums",
                count.tone === "danger" ? "bg-danger text-white" : "bg-white/10 text-sidebar-dim",
              )}
            >
              {count.value}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export function AppLayout() {
  const { session, logout } = useAuth();
  // #299: was darf dieser Mensch — der Server sagt es, die Navigation zeigt es.
  const userContext = useUserContext();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  useTokenRefresh();

  // Live-Counts für die Sidebar-Badges (Problem-Agents / Offline-Channels+Nodes).
  // #299 (Review M-3 + R2 L-D): nur abrufen, was dieser Mensch sehen darf — und ERST,
  // wenn der Kontext da ist. Die "im Zweifel anzeigen"-Regel gilt der ANZEIGE; ein Abruf
  // auf Verdacht kostet nur 403er (drei pro Seitenladen fuer einen plugin-author).
  const agents = useAgents({ enabled: darfJetzt(userContext, "agents.view") });
  const channels = useChannels({ enabled: darfJetzt(userContext, "channels.view") });
  const nodes = useNodes({ enabled: darfJetzt(userContext, "nodes.view") });
  const problemCount = (agents.data ?? []).filter((a) => !a.revokedAt && PROBLEM_PHASES.includes(a.lifecyclePhase)).length;
  const offlineCh = (channels.data ?? []).filter((c) => c.status === "offline").length;
  const offlineNd = (nodes.data ?? []).filter((n) => n.status === "offline").length;
  const counts: Record<string, Count | undefined> = {
    agents: problemCount > 0 ? { value: problemCount, tone: "danger" } : { value: agents.data?.length ?? 0, tone: "muted" },
    channels: offlineCh > 0 ? { value: offlineCh, tone: "danger" } : { value: channels.data?.length ?? 0, tone: "muted" },
    nodes: offlineNd > 0 ? { value: offlineNd, tone: "danger" } : { value: nodes.data?.length ?? 0, tone: "muted" },
  };

  const seg = pathname.split("/")[1] ?? "";
  const title = TITLES[seg] ?? "Elyvers";

  const [searchQ, setSearchQ] = useState("");
  function handleSearch(e: FormEvent) {
    e.preventDefault();
    const q = searchQ.trim();
    navigate(q ? `/agents?q=${encodeURIComponent(q)}` : "/agents");
  }

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex min-h-screen bg-bg">
      {/* ---------- Sidebar ---------- */}
      <aside className="sticky top-0 flex h-screen w-[244px] shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-text">
        <div className="flex items-center gap-3 px-[18px] pb-3.5 pt-[18px]">
          <MeshLogo />
          <div className="leading-[1.15]">
            <div className="text-[14.5px] font-extrabold tracking-tight text-sidebar-brand">Elyvers</div>
            <div className="text-[10.5px] font-semibold uppercase tracking-[.06em] text-sidebar-dim">Admin-Konsole</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pb-5 pt-1.5" role="navigation">
          {/* #299 (Review R2 L-1): Wer gar keinen Bereich freigeschaltet hat, sah eine
              LEERE Leiste und wurde von der Anmeldung auf eine 403-Seite geschickt — ohne
              Erklaerung und ohne Rueckweg. Ein Satz ist billiger als Ratlosigkeit. */}
          {userContext.isSuccess && sichtbareNavGruppen(userContext.data).length === 0 && (
            <div className="px-2.5 py-3 text-[12px] leading-relaxed text-sidebar-dim" data-testid="nav-leer">
              Für dieses Konto ist hier kein Bereich freigegeben. Die persönliche Sicht auf
              deine Agents liegt in der User-App.
            </div>
          )}
          {sichtbareNavGruppen(userContext.data).map((sec, i) => (
            <div key={i} className={sec.group ? "mt-[18px]" : "mt-1"}>
              {sec.group && (
                <div className="px-2.5 pb-[7px] text-[10.5px] font-bold uppercase tracking-[.09em] text-sidebar-dim">
                  {sec.group}
                </div>
              )}
              {sec.items.map((item) => (
                <NavRow key={item.to} item={item} count={item.countKey ? counts[item.countKey] : undefined} />
              ))}
            </div>
          ))}
        </nav>

        <div className="border-t border-sidebar-border p-3">
          <div className="flex items-center gap-2 rounded-lg bg-sidebar-active px-2.5 py-2">
            <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-ok shadow-[0_0_0_3px_color-mix(in_srgb,var(--ok)_18%,transparent)]" />
            <div className="text-[11.5px] font-semibold text-sidebar-text">System operativ</div>
          </div>
        </div>
      </aside>

      {/* ---------- Main column ---------- */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-[60px] shrink-0 items-center justify-between border-b border-border bg-surface px-[26px]">
          <h1 className="m-0 text-[17px] font-bold tracking-tight text-text">{title}</h1>
          {session && (
            <div className="flex items-center gap-2.5">
              {/* Globale Suche → Agents-Suche */}
              <form onSubmit={handleSearch} className="relative hidden w-[240px] md:block">
                <Search className="pointer-events-none absolute left-[11px] top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-text3" />
                <input
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="Agent suchen…"
                  data-testid="topbar-search"
                  className="h-9 w-full rounded-lg border border-border bg-surface2 pl-[34px] pr-12 text-[13px] text-text outline-none placeholder:text-text3 focus:border-accent-border"
                />
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded border border-border-strong px-1.5 py-px text-[11px] font-semibold text-text3">⌘K</span>
              </form>
              <button
                onClick={toggleTheme}
                aria-label="Theme wechseln"
                title="Theme wechseln"
                className="grid h-[30px] w-[30px] place-items-center rounded-[7px] text-text3 transition-colors hover:bg-surface3 hover:text-text"
              >
                {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </button>
              {/* Alarm: roter Dot bei Problem-Agents → gefilterte Liste */}
              <button
                onClick={() => navigate(problemCount > 0 ? "/agents?phase=problem" : "/agents")}
                aria-label="Alarme"
                title={problemCount > 0 ? `${problemCount} Agent(s) mit Problem` : "Keine Alarme"}
                data-testid="topbar-alerts"
                className="relative grid h-[30px] w-[30px] place-items-center rounded-[7px] text-text3 transition-colors hover:bg-surface3 hover:text-text"
              >
                <Bell className="h-4 w-4" />
                {problemCount > 0 && (
                  <span className="absolute right-[5px] top-[4px] h-[7px] w-[7px] rounded-full border-[1.5px] border-surface bg-danger" />
                )}
              </button>
              <div className="mx-0.5 h-6 w-px bg-border" />
              <div className="pl-0.5 text-right leading-tight">
                <div className="text-[12.5px] font-semibold text-text">{session.user.email}</div>
                <div className="text-[11px] text-text3">{session.user.role === "admin" ? "Administrator" : session.user.role}</div>
              </div>
              <div className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-full bg-gradient-to-br from-accent to-[#7c93f5] text-[13px] font-bold text-white">
                {session.user.email.slice(0, 2).toUpperCase()}
              </div>
              <button
                onClick={handleLogout}
                aria-label="Logout"
                title="Logout"
                data-testid="logout-button"
                className="grid h-[30px] w-[30px] place-items-center rounded-[7px] text-text3 transition-colors hover:bg-surface3 hover:text-text"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          )}
        </header>

        <main className="w-full flex-1 px-[30px] pb-[60px] pt-[26px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
